import { type VariantProps, cva } from 'class-variance-authority'
import React, { forwardRef } from 'react'
import { cn } from '~/src/lib/utils'

const blurContainer = cva('', {
  variants: {
    blur: {
      none: 'blur-none',
      xs: 'blur-[1px]',
      sm: 'blur-[2px]',
      default: 'blur-[4px]',
      lg: 'blur-[8px]',
      xl: 'blur-[12px]',
    },
  },
  defaultVariants: {
    blur: 'none',
  },
})

interface AnimatedBlurContainerProps
  extends VariantProps<typeof blurContainer>,
    React.HTMLAttributes<HTMLDivElement> {
  className?: string
  borderClassName?: string
  childrenClassName?: string
  borderWidth?: number
  glow?: boolean | 'hover'
  pulse?: boolean
  pulseInterval?: number
  pulseOnce?: boolean
  as?: React.ElementType
  respectDarkMode?: boolean
}

const AnimatedBlurContainer = forwardRef<
  HTMLElement,
  AnimatedBlurContainerProps
>(
  (
    {
      children,
      className,
      borderClassName,
      childrenClassName,
      blur,
      borderWidth = 2,
      glow,
      pulse,
      pulseInterval = 10000,
      pulseOnce,
      as,
      respectDarkMode = false,
      ...rest
    },
    ref
  ) => {
    const Component = as || 'div'

    return (
      <Component
        className={cn('group relative', className)}
        ref={ref}
        {...rest}
      >
        <div
          className={cn(
            'pointer-events-none absolute',
            'animate-border border-transparent',
            respectDarkMode
              ? '[background:linear-gradient(45deg,theme(colors.white),theme(colors.white),theme(colors.white))_padding-box,conic-gradient(from_var(--border-angle),#BC82F3_0%,#F5B9EA_12.5%,#8D99FF_25%,#AA6EEE_37.5%,#FF6778_50%,#FFBA71_62.5%,#C686FF_75%,#BC82F3_87.5%,#BC82F3_100%)_border-box] dark:[background:linear-gradient(45deg,theme(colors.gray.800),theme(colors.gray.800),theme(colors.gray.800))_padding-box,conic-gradient(from_var(--border-angle),#BC82F3_0%,#F5B9EA_12.5%,#8D99FF_25%,#AA6EEE_37.5%,#FF6778_50%,#FFBA71_62.5%,#C686FF_75%,#BC82F3_87.5%,#BC82F3_100%)_border-box]'
              : '[background:linear-gradient(45deg,#fff,#fff,#fff)_padding-box,conic-gradient(from_var(--border-angle),#BC82F3_0%,#F5B9EA_12.5%,#8D99FF_25%,#AA6EEE_37.5%,#FF6778_50%,#FFBA71_62.5%,#C686FF_75%,#BC82F3_87.5%,#BC82F3_100%)_border-box]',
            blurContainer({ blur }),
            pulse && 'animate-fade-pulse',
            pulseOnce &&
              'animate-pulse-once duration-3000 delay-1500 opacity-0',
            borderClassName,
            {
              'group-hover:shadow-[0_0_15px_5px_rgba(188,130,243,1)]':
                glow === 'hover',
            }
          )}
          style={
            {
              inset: -1 * borderWidth,
              borderWidth,
              '--pulse-duration': pulse ? `${pulseInterval}ms` : undefined,
              // boxShadow: glow
              //   ? '0 0 15px 5px rgba(188, 130, 243, 1)'
              //   : undefined,
            } as React.CSSProperties
          }
        />
        <div className={cn('relative', childrenClassName)}>{children}</div>
      </Component>
    )
  }
)

AnimatedBlurContainer.displayName = 'AnimatedBlurContainer'

export default AnimatedBlurContainer
